/**
 * Engine Atlas cross-link URL constants per Atlas Day 2 Phase 5.
 *
 * ADR 36 + 38 hardcoded prod-domain convention (matches FormulaEditor.tsx
 * ARBI_CHANNELS_PATHS_URL + ARBI_PARAMS_DEFAULTS_URL precedent). Per-file
 * URL constants with explicit anchor IDs verified against Settings.tsx +
 * Directory.tsx SectionId enums at Atlas Day 2 Phase 0 #14-#16.
 *
 * Anchor naming convention: Settings.tsx + Directory.tsx render section
 * IDs as `section-${id}` via id={`section-${id}`} template at L207 (and
 * equivalent locations). All Atlas cross-link constants reference these
 * resolved IDs verbatim.
 *
 * D5 lock 2026-05-27: Engine Tuning has no separate Settings SectionId
 * (sub-section within section-params per Day 86 A6 PARAM_GROUPS_SETTINGS).
 * URL_SETTINGS_PARAMS_ENGINE_TUNING resolves to section-params parent
 * anchor; constant name reflects target-honesty (parent + subsection).
 */

const ARBI_ROOT_BASE = 'https://arbi.ironcladsupply.co';
const ARBI_DEV_BASE = 'https://arbi-dev.ironcladsupply.co';

// ── Settings cross-links (Settings.tsx SectionId enum L74-94) ──────────────

export const URL_SETTINGS_PARAMS = `${ARBI_ROOT_BASE}/settings#section-params`;
export const URL_SETTINGS_COST_LINE_ITEMS = `${ARBI_ROOT_BASE}/settings#section-cost-line-items`;
export const URL_SETTINGS_CHANNELS_AND_PATHS = `${ARBI_ROOT_BASE}/settings#section-channels-and-paths`;
/** @public — pinned-by-source-assertion (U11 census C1): root tests assert this export's source text. */
export const URL_SETTINGS_DISTRIBUTOR_SCOPE = `${ARBI_ROOT_BASE}/settings#section-distributor-scope`;

/**
 * D5 lock (2026-05-27): Engine Tuning is a subsection within
 * section-params per Day 86 A6 PARAM_GROUPS_SETTINGS; no separate
 * SectionId exists in Settings.tsx. Resolves to parent anchor.
 * Future Settings refactor may add explicit section-engine-tuning
 * SectionId (out of Atlas Day 2 scope; BACKLOG candidate for Atlas Day 3
 * close-out sweep).
 */
export const URL_SETTINGS_PARAMS_ENGINE_TUNING = URL_SETTINGS_PARAMS;

// ── Directory cross-links (Directory.tsx SectionId enum L57-64) ────────────

export const URL_DIRECTORY_DISTRIBUTORS = `${ARBI_ROOT_BASE}/directory#section-distributors`;
/** @public — pinned-by-source-assertion (U11 census C1): root tests assert this export's source text. */
export const URL_DIRECTORY_SUPPLIERS = `${ARBI_ROOT_BASE}/directory#section-suppliers`;
export const URL_DIRECTORY_BRANDS = `${ARBI_ROOT_BASE}/directory#section-brands`;
export const URL_DIRECTORY_MANUFACTURERS = `${ARBI_ROOT_BASE}/directory#section-manufacturers`;
/** @public — pinned-by-source-assertion (U11 census C1): root tests assert this export's source text. */
export const URL_DIRECTORY_CATEGORIES = `${ARBI_ROOT_BASE}/directory#section-categories`;

// ── Engine Editor dedicated edit route anchors (Day 3 P4 migration targets) ──
//
// The deprecated arbi-dev legacy-admin URL constant was removed at Day 3 P5
// per BuyScoreWeights deprecation (Atlas Inspect Cross-links migrated to
// URL_DEV_ENGINE_EDITOR_* per Sol option (a) at Day 3 P0.4; orphan re-grep at
// P5 confirmed zero consumers). Comment scrubbed of literal constant name at
// Day 4 Phase A per defensive-cleanup discipline (so prior test assertion
// can no longer satisfy itself via comment text).
//
// Atlas read-altitude → Engine Editor write-altitude cross-links per
// scope-doc §2 lines 261-262 cross-link migration commitments. Absolute URL +
// anchor fragment preserves Atlas `<a href>` external-link shape + native
// hash-scroll on arrival per Sol option (a) at Day 3 P0.4.

export const URL_DEV_ENGINE_EDITOR_WEIGHT_COMPONENTS = `${ARBI_DEV_BASE}/admin/engine-editor/weight-components#weight-components`;
export const URL_DEV_ENGINE_EDITOR_BUY_SCORE_COMPOSITION = `${ARBI_DEV_BASE}/admin/engine-editor/weight-components#buy-score-composition`;
